import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import usersApi from "../../api/users";

export interface CurrentUser {
  id?: number;
  userId?: number | null;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  orgid?: number;
  orgType?: string;
  orgRole?: string;
  genericRole?: string;
  roleId?: number;
  roleName?: string;
  orgLogo?: string | string[];
  [key: string]: any;
}

interface CurrentUserState {
  data: CurrentUser | null;
  loading: boolean;
  error: string | null;
}

const initialState: CurrentUserState = {
  data: null,
  loading: false,
  error: null,
};

/**
 * Fetches the signed-in user — called after login and on
 * any page that needs the user's profile/role/org info.
 */
export const fetchCurrentUser = createAsyncThunk(
  "currentUser/fetchCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await usersApi.getCurrentUser();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load current user"
      );
    }
  }
);

const slice = createSlice({
  name: "currentUser",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<CurrentUser | null>) => {
      state.data = action.payload;
    },
    clearUser: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
    logout: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUser, clearUser, logout } = slice.actions;
export default slice.reducer;
